import { useState, useEffect, useRef, Fragment, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "./api";
import Simulator from "./Simulator";
import Onboarding from "./Onboarding";
import Login from "./Login";
import LiveTransactionMap from "./LiveTransactionMap";
import { STATUS_STYLES, TX_STATUS_FILTERS, statusStyle } from "./txStatus";

const DASHBOARD_REFRESH_SECONDS = 15;

// PERF-6: el polling periódico dispara un fetch nuevo cada 15s sin esperar
// a que termine el anterior. Sin abortar el que quedó en vuelo, una respuesta
// tardía puede resolver después de una más nueva y pisar el estado con datos
// viejos, además de seguir consumiendo ancho de banda/conexiones ya inútiles.
const isAbortError = (e) => e && e.name === "AbortError";

const ROLE_PERMISSIONS = {
  admin:    { view_support: true, view_business: true, modify_params: true, delete_user: true },
  support:  { view_support: true, view_business: false, modify_params: true, delete_user: false },
  business: { view_support: false, view_business: true, modify_params: false, delete_user: false },
};

const PROVIDER_STYLES = {
  online:         { dot: "#34d399", label: "Online" },
  degraded:       { dot: "#fbbf24", label: "Degradado" },
  offline:        { dot: "#f87171", label: "Offline" },
  not_configured: { dot: "#4a6a4a", label: "Sin configurar" },
  adapter_error:  { dot: "#f87171", label: "Error de adaptador" },
};

const PROVIDER_CARD_LABELS = {
  bot: "Bot",
  db: "Base de datos",
  redis: "Redis",
  whatsapp: "WhatsApp",
  kyc: "KYC",
};

// Fila 1: infraestructura core + canales. Fila 2: providers financieros
// (todo lo que no está en esta lista, en el orden que devuelva el backend).
const SYSTEM_STATUS_ROW1_KEYS = ["bot", "db", "redis", "whatsapp", "kyc"];

const SEMAPHORE_COLORS = {
  green:  "#34d399",
  yellow: "#fbbf24",
  orange: "#fb923c",
  red:    "#f87171",
};

const CORE_STATUS_KEYS = ["bot", "db", "redis"];

function computeSystemLiveStatus(status) {
  if (!status) return null;
  if (CORE_STATUS_KEYS.some(k => status[k] !== "online")) return "red";
  // Solo cuentan los proveedores activos; "not_configured" es un estado
  // esperado (ej. esqueletos sin credenciales) y no cuenta como caido.
  const providersDown = (status.providers || []).filter(
    p => p.status === "active" && p.health !== "online" && p.health !== "not_configured"
  ).length;
  if (providersDown === 0) return "green";
  if (providersDown === 1) return "yellow";
  return "orange";
}

const KYC_LABELS = {
  pending:  { label: "Pendiente", color: "#fbbf24" },
  verified: { label: "Verificado", color: "#34d399" },
  rejected: { label: "Rechazado", color: "#f87171" },
};

const PARAM_LABELS = {
  inactivity_day_seconds:       { label: "Sin mensajes de 9 a 18 hs antes de alertar", unit: "seg" },
  inactivity_night_seconds:     { label: "Sin mensajes de 18 a 9 hs antes de alertar", unit: "seg" },
  provider_down_threshold:      { label: "Fallos para marcar proveedor caído", unit: "fallos" },
  alert_cooldown_seconds:       { label: "Cooldown entre alertas", unit: "seg" },
  tx_failure_threshold:         { label: "Txs fallidas para alertar", unit: "txs" },
  fee_max_attempts:             { label: "Intentos de cobro de fee antes de pasar a pérdida", unit: "intentos" },
  pending_nudge_cooldown_seconds: { label: "Reenvío de aviso de pendiente", unit: "seg" },
};

const fmt = (n) => `$${Number(n).toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n).toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// amount_usd guarda el monto en moneda de origen (o 0,01 de marcador en depositos
// de puntos): el valor real es source_amount + source_currency.
const txAmountLabel = (tx) => tx.source_amount == null
  ? fmtNum(tx.amount_usd)
  : `${fmtNum(tx.source_amount)} ${tx.source_currency || ""}`.trim();
const txFeeLabel = (tx) => `${fmtNum(tx.fee_usd)} ${tx.source_currency || ""}`.trim();
const txReceivedLabel = (tx) => (tx.target_amount != null && tx.target_currency && tx.target_currency !== tx.source_currency)
  ? `${fmtNum(tx.target_amount)} ${tx.target_currency}` : "";

// UX-5: props para que un div/tr con onClick sea operable por teclado (Enter/Espacio),
// no solo con mouse.
const clickable = (onClick) => ({
  role: "button",
  tabIndex: 0,
  onClick,
  onKeyDown: (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  },
});

// ── Components ─────────────────────────────────────────────

function IconTransacciones({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8h13l-3.5-3.5M18 16H5l3.5 3.5" />
    </svg>
  );
}

function IconUsuarios({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="3.3" />
      <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" />
    </svg>
  );
}

function IconAuditoria({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5.5" y="4" width="13" height="16" rx="1.5" />
      <path d="M9 3.5h6v2H9zM8.5 10h7M8.5 13.5h7M8.5 17h4" />
    </svg>
  );
}

const SUPPORT_SUB_TABS = [
  { key: "transacciones", label: "Transacciones", icon: IconTransacciones },
  { key: "usuarios", label: "Usuarios", icon: IconUsuarios },
  { key: "audit", label: "Auditoría", icon: IconAuditoria },
];

function StatusDot({ status }) {
  const s = PROVIDER_STYLES[status] || PROVIDER_STYLES.offline;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.dot, display: "inline-block", boxShadow: `0 0 6px ${s.dot}` }} />
      <span style={{ fontSize: 11, color: s.dot }}>{s.label}</span>
    </span>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: "#0d120d", border: "1px solid #1a2a1a", borderRadius: 2, padding: "20px 24px" }}>
      <div style={{ fontSize: 10, color: "#4a6a4a", letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>{label}</div>
      <div style={{ fontSize: 26, color: accent || "#e8f5e8", fontWeight: 300, letterSpacing: -1 }}>{value}</div>
    </div>
  );
}

function ConfirmModal({ paramKey, oldValue, newValue, onConfirm, onCancel }) {
  const meta = PARAM_LABELS[paramKey] || { label: paramKey, unit: "" };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div style={{ background: "#0d120d", border: "1px solid #34d399", borderRadius: 2, padding: "32px 40px", width: 420 }}>
        <div style={{ fontSize: 10, color: "#34d399", letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>Confirmar cambio</div>
        <div style={{ fontSize: 13, color: "#c8e6c8", marginBottom: 4 }}>{meta.label}</div>
        <div style={{ fontSize: 10, color: "#3a5a3a", marginBottom: 20 }}>{meta.unit}</div>
        <div style={{ display: "flex", gap: 16, marginBottom: 24, alignItems: "center" }}>
          <div style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 2, padding: "8px 20px", fontSize: 20, color: "#6b7280" }}>{oldValue}</div>
          <div style={{ color: "#4a6a4a", fontSize: 18 }}>→</div>
          <div style={{ background: "#0d2010", border: "1px solid #34d399", borderRadius: 2, padding: "8px 20px", fontSize: 20, color: "#34d399" }}>{newValue}</div>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "10px", background: "transparent", border: "1px solid #2a3a2a", borderRadius: 2, color: "#6b7280", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: "10px", background: "#1a3a1a", border: "1px solid #34d399", borderRadius: 2, color: "#34d399", fontSize: 12, cursor: "pointer" }}>Confirmar</button>
        </div>
      </div>
    </div>
  );
}

function MaintenanceConfirmModal({ active, onConfirm, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div style={{ background: "#0d120d", border: `1px solid ${active ? "#f87171" : "#34d399"}`, borderRadius: 2, padding: "32px 40px", width: 420 }}>
        <div style={{ fontSize: 10, color: active ? "#f87171" : "#34d399", letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>Confirmar cambio</div>
        <div style={{ fontSize: 13, color: "#c8e6c8", marginBottom: 24, lineHeight: 1.5 }}>
          {active
            ? "¿Activar modo mantenimiento? El bot dejará de responder a usuarios normales — solo los teléfonos habilitados van a seguir operando."
            : "¿Desactivar modo mantenimiento? El bot vuelve a operar normalmente para todos los usuarios."}
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "10px", background: "transparent", border: "1px solid #2a3a2a", borderRadius: 2, color: "#6b7280", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: "10px", background: active ? "#3a1a1a" : "#1a3a1a", border: `1px solid ${active ? "#f87171" : "#34d399"}`, borderRadius: 2, color: active ? "#f87171" : "#34d399", fontSize: 12, cursor: "pointer" }}>Confirmar</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, live, liveStatus, collapsible, defaultCollapsed, children }) {
  const [collapsed, setCollapsed] = useState(!!collapsible && !!defaultCollapsed);
  const liveColor = SEMAPHORE_COLORS[liveStatus] || "#4a6a4a";
  return (
    <div style={{ marginBottom: 24 }}>
      <div {...(collapsible ? clickable(() => setCollapsed(c => !c)) : {})}
        style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, cursor: collapsible ? "pointer" : "default", userSelect: collapsible ? "none" : "auto" }}>
        {collapsible && (
          <span style={{ fontSize: 9, color: "#4a6a4a", display: "inline-block", transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>▾</span>
        )}
        <div style={{ fontSize: 10, letterSpacing: 3, color: "#4a6a4a", textTransform: "uppercase" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 10, color: "#2a3a2a" }}>— {subtitle}</div>}
        {live && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginLeft: "auto" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: liveColor, display: "inline-block", animation: "pulse 2s infinite" }} />
            <span style={{ fontSize: 9, color: liveColor, letterSpacing: 2 }}>LIVE</span>
          </div>
        )}
      </div>
      {(!collapsible || !collapsed) && children}
    </div>
  );
}

function SupportView({ user }) {
  const [status, setStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [params, setParams] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  const [txFilter, setTxFilter] = useState("all");
  const [txFlaggedOnly, setTxFlaggedOnly] = useState(false);
  const [selectedTxId, setSelectedTxId] = useState(null);
  const [selectedTxEvents, setSelectedTxEvents] = useState([]);
  const txRequestIdRef = useRef(0);
  const [editParam, setEditParam] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [confirmModal, setConfirmModal] = useState(null);
  const [subTab, setSubTab] = useState("transacciones");
  const [maintenance, setMaintenance] = useState(null);
  const [maintMessageDraft, setMaintMessageDraft] = useState("");
  const [maintPhonesDraft, setMaintPhonesDraft] = useState("");
  const [maintConfirm, setMaintConfirm] = useState(null);
  const [providerConfirm, setProviderConfirm] = useState(null);
  const [providerBusy, setProviderBusy] = useState(false);

  const confirmProviderToggle = async () => {
    const p = providerConfirm;
    setProviderBusy(true);
    try {
      await api.setProviderStatus(p.code, p.status === "active" ? "inactive" : "active");
      setProviderConfirm(null);
      api.getStatus().then(setStatus).catch(console.error);
    } catch (e) {
      setProviderConfirm({ ...p, error: e.message });
    } finally {
      setProviderBusy(false);
    }
  };

  const canModify = (ROLE_PERMISSIONS[user.role] || {}).modify_params;
  const canDeleteUser = (ROLE_PERMISSIONS[user.role] || {}).delete_user;
  const systemLiveStatus = computeSystemLiveStatus(status);

  useEffect(() => {
    let controller = new AbortController();
    const load = () => {
      controller.abort();
      controller = new AbortController();
      const { signal } = controller;
      api.getStatus(signal).then(setStatus).catch(e => !isAbortError(e) && console.error(e));
      api.getParams(signal).then(setParams).catch(e => !isAbortError(e) && console.error(e));
      api.getAuditLog(signal).then(setAuditLog).catch(e => !isAbortError(e) && console.error(e));
      api.getTransactions(txFilter, txFlaggedOnly, signal).then(setTransactions).catch(e => !isAbortError(e) && console.error(e));
    };
    load();
    const interval = setInterval(load, DASHBOARD_REFRESH_SECONDS * 1000);
    return () => { clearInterval(interval); controller.abort(); };
  }, [txFilter, txFlaggedOnly]);

  useEffect(() => {
    api.getMaintenance().then(m => {
      setMaintenance(m);
      setMaintMessageDraft(m.message);
      setMaintPhonesDraft(m.allowed_phones.join(", "));
    }).catch(console.error);
  }, []);

  const handleMaintenanceConfirm = async () => {
    const { active } = maintConfirm;
    const phones = maintPhonesDraft.split(",").map(p => p.trim()).filter(Boolean);
    try {
      const result = await api.setMaintenance(active, maintMessageDraft, phones);
      setMaintenance({ active: result.active, message: result.message, allowed_phones: phones });
    } catch (e) {
      console.error(e);
    }
    setMaintConfirm(null);
  };

  const handleParamSave = (key) => {
    if (!/^\d+$/.test(editValue.trim())) return;
    const val = parseInt(editValue, 10);
    if (isNaN(val) || val <= 0) return;
    setConfirmModal({ key, oldValue: params[key], newValue: val });
    setEditParam(null);
  };

  const handleConfirm = async () => {
    const { key, newValue } = confirmModal;
    try {
      await api.updateParam(key, newValue);
      setParams(p => ({ ...p, [key]: newValue }));
      const log = await api.getAuditLog();
      setAuditLog(log);
    } catch (e) {
      console.error(e);
    }
    setConfirmModal(null);
  };

  const loadTransactionEvents = async (txId) => {
    // BUG-7: dos clicks seguidos en filas distintas disparaban dos fetches en
    // paralelo; el que respondía último pisaba el detalle mostrado, sin importar
    // si correspondía a la fila que quedó seleccionada. txRequestIdRef descarta
    // cualquier respuesta que ya no sea la del último click.
    const requestId = ++txRequestIdRef.current;
    setSelectedTxId(txId);
    setSelectedTxEvents([]);
    try {
      const events = await api.getTransactionEvents(txId);
      if (txRequestIdRef.current !== requestId) return;
      setSelectedTxEvents(events);
    } catch (e) {
      console.error(e);
      if (txRequestIdRef.current !== requestId) return;
      setSelectedTxId(null);
      setSelectedTxEvents([]);
    }
  };

  const toggleTxDetails = (txId) => {
    if (selectedTxId === txId) {
      txRequestIdRef.current++; // invalida cualquier fetch en vuelo para esta fila
      setSelectedTxId(null);
      setSelectedTxEvents([]);
      return;
    }
    loadTransactionEvents(txId);
  };

  const getEventMetadata = (metadata) => {
    if (!metadata) return {};
    if (typeof metadata === "string") {
      try {
        const parsed = JSON.parse(metadata);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    }
    return typeof metadata === "object" ? metadata : {};
  };

  return (
    <div className="support-view-root">
      {confirmModal && (
        <ConfirmModal
          paramKey={confirmModal.key}
          oldValue={confirmModal.oldValue}
          newValue={confirmModal.newValue}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {maintConfirm && (
        <MaintenanceConfirmModal
          active={maintConfirm.active}
          onConfirm={handleMaintenanceConfirm}
          onCancel={() => setMaintConfirm(null)}
        />
      )}

      <Section title="Modo mantenimiento" collapsible defaultCollapsed>
        {maintenance && (
          <div style={{ background: "#111811", border: `1px solid ${maintenance.active ? "#4a1a1a" : "#1a2a1a"}`, borderRadius: 2, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: maintenance.active ? "#f87171" : "#34d399", display: "inline-block" }} />
              <span style={{ fontSize: 12, color: maintenance.active ? "#f87171" : "#34d399" }}>
                {maintenance.active ? "Activo — el bot no responde a usuarios normales" : "Inactivo — el bot opera normalmente"}
              </span>
            </div>
            {canModify && (
              <>
                <div>
                  <div style={{ fontSize: 10, color: "#6b9a6b", marginBottom: 4 }}>Mensaje mostrado durante el mantenimiento</div>
                  <textarea value={maintMessageDraft} onChange={e => setMaintMessageDraft(e.target.value)} rows={2}
                    style={{ width: "100%", background: "#0d120d", border: "1px solid #1a2a1a", borderRadius: 2, color: "#c8e6c8", fontSize: 12, padding: 8, fontFamily: "inherit", resize: "vertical" }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "#6b9a6b", marginBottom: 4 }}>Teléfonos habilitados para probar (separados por coma)</div>
                  <textarea value={maintPhonesDraft} onChange={e => setMaintPhonesDraft(e.target.value)} rows={1}
                    placeholder="+59899123456, +5491112345678"
                    style={{ width: "100%", background: "#0d120d", border: "1px solid #1a2a1a", borderRadius: 2, color: "#c8e6c8", fontSize: 12, padding: 8, fontFamily: "inherit", resize: "vertical" }} />
                </div>
                <button onClick={() => setMaintConfirm({ active: !maintenance.active })}
                  style={{ alignSelf: "flex-start", padding: "8px 16px", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", background: "transparent", border: `1px solid ${maintenance.active ? "#34d399" : "#4a1a1a"}`, borderRadius: 2, color: maintenance.active ? "#34d399" : "#f87171", cursor: "pointer" }}>
                  {maintenance.active ? "Desactivar mantenimiento" : "Activar mantenimiento"}
                </button>
              </>
            )}
          </div>
        )}
      </Section>

      <Section title="Estado del sistema" live liveStatus={systemLiveStatus} collapsible defaultCollapsed>
        {status && (() => {
          const toCard = key => ({
            key,
            label: PROVIDER_CARD_LABELS[key] || key,
            val: status[key],
            sub: key === "bot" ? status.last_activity : undefined,
          });
          const row1 = SYSTEM_STATUS_ROW1_KEYS.filter(k => k in status).map(toCard);
          const row2 = Object.keys(status)
            .filter(k => k !== "last_activity" && k !== "providers" && !SYSTEM_STATUS_ROW1_KEYS.includes(k))
            .map(toCard);
          const grid = cards => (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
              {cards.map(s => (
                <div key={s.key} style={{ background: "#111811", border: "1px solid #1a2a1a", borderRadius: 2, padding: "14px 16px" }}>
                  <div style={{ fontSize: 10, color: "#4a6a4a", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>{s.label}</div>
                  <StatusDot status={s.val} />
                  {s.sub && <div style={{ fontSize: 10, color: "#4a6a4a", marginTop: 4 }}>{s.sub}</div>}
                </div>
              ))}
            </div>
          );
          return (
            <div style={{ display: "grid", gap: 12 }}>
              {grid(row1)}
              {row2.length > 0 && grid(row2)}
              {(status.providers || []).length > 0 && (
                <>
                  <div style={{ fontSize: 9, color: "#3a5a3a", letterSpacing: 2, textTransform: "uppercase", marginTop: 4 }}>Proveedores financieros</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                    {status.providers.map(p => (
                      <div key={p.code} style={{ background: "#111811", border: "1px solid #1a2a1a", borderRadius: 2, padding: "14px 16px", opacity: p.status === "active" ? 1 : 0.6 }}>
                        <div style={{ fontSize: 10, color: "#4a6a4a", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>{p.display_name}</div>
                        <StatusDot status={p.health} />
                        <div style={{ fontSize: 10, color: p.status === "active" ? "#34d399" : "#6b7280", marginTop: 6 }}>
                          {p.status === "active" ? "Activo" : "Inactivo"}
                        </div>
                        {canModify && (
                          <button
                            onClick={() => setProviderConfirm(p)}
                            style={{ marginTop: 10, padding: "4px 10px", background: "transparent", border: `1px solid ${p.status === "active" ? "#f87171" : "#34d399"}`, borderRadius: 2, color: p.status === "active" ? "#f87171" : "#34d399", fontSize: 10, cursor: "pointer" }}
                          >
                            {p.status === "active" ? "Desactivar" : "Activar"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })()}
      </Section>

      {providerConfirm && (
        <ProviderToggleModal
          provider={providerConfirm}
          busy={providerBusy}
          onConfirm={confirmProviderToggle}
          onCancel={() => setProviderConfirm(null)}
        />
      )}

      <Section title="Parámetros de monitoreo" collapsible defaultCollapsed>
        <div style={{ fontSize: 10, color: "#2a3a2a", marginBottom: 10 }}>{canModify ? "Click en valor para editar" : "Solo lectura"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          {Object.entries(params).map(([key, value]) => {
            const meta = PARAM_LABELS[key] || { label: key, unit: "" };
            return (
              <div key={key} style={{ background: "#111811", border: "1px solid #1a2a1a", borderRadius: 2, padding: "12px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
                <div>
                  <div style={{ fontSize: 10, color: "#6b9a6b", lineHeight: 1.4 }}>{meta.label}</div>
                  <div style={{ fontSize: 9, color: "#3a5a3a", marginTop: 2 }}>{meta.unit}</div>
                </div>
                {editParam === key ? (
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
                    <input value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus
                      onKeyDown={e => { if (e.key === "Enter") handleParamSave(key); if (e.key === "Escape") setEditParam(null); }}
                      style={{ width: 70, background: "#0d200d", border: "1px solid #34d399", borderRadius: 2, padding: "3px 6px", color: "#34d399", fontSize: 14, outline: "none", textAlign: "right" }} />
                    <button onClick={() => handleParamSave(key)} style={{ background: "#1a3a1a", border: "1px solid #34d399", borderRadius: 2, color: "#34d399", padding: "3px 8px", fontSize: 9, cursor: "pointer" }}>OK</button>
                    <button onClick={() => setEditParam(null)} style={{ background: "transparent", border: "1px solid #2a3a2a", borderRadius: 2, color: "#6b7280", padding: "3px 8px", fontSize: 9, cursor: "pointer" }}>✕</button>
                  </div>
                ) : (
                  <div {...(canModify ? clickable(() => { setEditParam(key); setEditValue(String(value)); }) : {})}
                    style={{ fontSize: 20, color: "#34d399", fontWeight: 300, cursor: canModify ? "pointer" : "default", marginTop: 4 }}
                    title={canModify ? "Click para editar" : ""}>
                    {value}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      <div className="subtab-bar-desktop" style={{ display: "flex", gap: 2, marginBottom: 20, borderBottom: "1px solid #1a2a1a" }}>
        {SUPPORT_SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} className="sub-tab-btn"
            style={{ padding: "8px 18px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", background: "transparent", border: "none", borderBottom: subTab === t.key ? "2px solid #34d399" : "2px solid transparent", color: subTab === t.key ? "#34d399" : "#4a6a4a", cursor: "pointer", marginBottom: -1, whiteSpace: "nowrap" }}>
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "transacciones" && (
      <div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          <select value={txFilter} onChange={e => setTxFilter(e.target.value)}
            style={{ background: "#0d120d", border: "1px solid #1a2a1a", borderRadius: 2, padding: "7px 10px", color: "#c8e6c8", fontSize: 12, outline: "none" }}>
            {TX_STATUS_FILTERS.map(f => (
              <option key={f} value={f}>{f === "all" ? "Todas" : STATUS_STYLES[f]?.label || f}</option>
            ))}
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#c8e6c8", cursor: "pointer" }}>
            <input type="checkbox" checked={txFlaggedOnly} onChange={e => setTxFlaggedOnly(e.target.checked)} />
            Solo marcadas para revisión (monto alto)
          </label>
        </div>
        {transactions.length === 0 ? (
          <div style={{ color: "#3a5a3a", fontSize: 12, padding: "24px 0" }}>No hay transacciones aún.</div>
        ) : (
          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #1a2a1a" }}>
                    {["ID", "Receptor", "Monto", "Fee", "Corredor", "Proveedor", "Estado", "Revisión", "Fecha"].map(h => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, letterSpacing: 2, color: "#3a5a3a", textTransform: "uppercase", fontWeight: 400 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, i) => {
                    const s = statusStyle(tx.status);
                    const isOpen = selectedTxId === tx.id;
                    return (
                      <Fragment key={tx.id}>
                        <tr {...clickable(() => toggleTxDetails(tx.id))} style={{ borderBottom: "1px solid #111811", background: isOpen ? "#0a160d" : i % 2 === 0 ? "transparent" : "#0a0f0a", cursor: "pointer" }}>
                          <td style={{ padding: "10px 12px", color: "#4a6a4a", fontFamily: "monospace", fontSize: 11 }}>{String(tx.id).slice(0, 8)}...</td>
                          <td style={{ padding: "10px 12px", color: "#8ab88a", fontFamily: "monospace", fontSize: 11 }}>{tx.recipient_phone}</td>
                          <td style={{ padding: "10px 12px", color: "#e8f5e8" }}>
                            {txAmountLabel(tx)}
                            {txReceivedLabel(tx) && <div style={{ color: "#6b9a6b", fontSize: 10 }}>{"→ " + txReceivedLabel(tx)}</div>}
                          </td>
                          <td style={{ padding: "10px 12px", color: "#6b9a6b" }}>{txFeeLabel(tx)}</td>
                          <td style={{ padding: "10px 12px", color: "#6b9a6b", fontFamily: "monospace" }}>{tx.corridor}</td>
                          <td style={{ padding: "10px 12px", color: "#4a6a4a" }}>{tx.provider || "—"}</td>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{ background: s.bg, color: s.text, padding: "3px 8px", borderRadius: 2, fontSize: 10 }}>{s.label}</span>
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            {tx.flagged_for_review && (
                              <span title="Monto alto — revisar manualmente" style={{ background: "#2b1a00", color: "#fbbf24", padding: "3px 8px", borderRadius: 2, fontSize: 10 }}>⚠ Revisar</span>
                            )}
                          </td>
                          <td style={{ padding: "10px 12px", color: "#3a5a3a", fontSize: 11 }}>{new Date(tx.created_at).toLocaleString("es-UY")}</td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={9} style={{ padding: 0, borderBottom: "1px solid #1a2a1a" }}>
                              <div style={{ background: "#0d120d", borderTop: "1px solid #1a2a1a", padding: 16 }}>
                                <div style={{ fontSize: 10, letterSpacing: 3, color: "#4a6a4a", textTransform: "uppercase", marginBottom: 12 }}>
                                  Historial de la transacción
                                </div>
                                <div style={{ fontSize: 11, color: "#8ab88a", fontFamily: "monospace", marginBottom: 12 }}>{tx.id}</div>
                                {tx.notes && (
                                  <div style={{ fontSize: 12, color: "#c8e6c8", marginBottom: 12, padding: "8px 10px", background: "#0a0f0a", border: "1px solid #1a2a1a", borderRadius: 2 }}>
                                    <span style={{ color: "#4a6a4a", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", marginRight: 8 }}>Nota</span>
                                    {tx.notes}
                                  </div>
                                )}
                                {selectedTxEvents.length === 0 ? (
                                  <div style={{ color: "#3a5a3a", fontSize: 12 }}>Sin eventos registrados.</div>
                                ) : (
                                  <div style={{ display: "grid", gap: 10 }}>
                                    {selectedTxEvents.map((event) => {
                                      const metadata = getEventMetadata(event.metadata);
                                      const metadataKeys = Object.keys(metadata || {});

                                      return (
                                        <div key={event.id} style={{ background: "#0a0f0a", border: "1px solid #1a2a1a", borderRadius: 2, padding: "10px 12px" }}>
                                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 6 }}>
                                            <span style={{ color: "#4a6a4a", fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>{event.actor}</span>
                                            <span style={{ color: "#6b9a6b" }}>→</span>
                                            <span style={{ color: "#34d399", fontSize: 11 }}>{event.from_state || "start"}</span>
                                            <span style={{ color: "#6b9a6b" }}>→</span>
                                            <span style={{ color: "#a3e635", fontSize: 11 }}>{event.to_state}</span>
                                          </div>
                                          <div style={{ color: "#3a5a3a", fontSize: 11, marginBottom: 4 }}>
                                            {new Date(event.created_at).toLocaleString("es-UY")}
                                          </div>
                                          {event.reason && <div style={{ color: "#fbbf24", fontSize: 11, marginBottom: 4 }}>Motivo: {event.reason}</div>}
                                          {metadataKeys.length > 0 && (
                                            <div style={{ color: "#8ab88a", fontSize: 11, whiteSpace: "pre-wrap" }}>
                                              {JSON.stringify(metadata, null, 2)}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      )}

      {subTab === "audit" && (
      <div>
        {auditLog.length === 0 ? (
          <div style={{ color: "#3a5a3a", fontSize: 12, padding: "24px 0" }}>Sin cambios registrados aún.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 500 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #1a2a1a" }}>
                {["Usuario", "Parámetro", "Anterior", "Nuevo", "Fecha"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, letterSpacing: 2, color: "#3a5a3a", textTransform: "uppercase", fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {auditLog.map((a, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #111811" }}>
                  <td style={{ padding: "10px 12px", color: "#8ab88a" }}>{a.full_name}</td>
                  <td style={{ padding: "10px 12px", color: "#6b9a6b", fontSize: 11 }}>{PARAM_LABELS[a.param_key]?.label || a.param_key}</td>
                  <td style={{ padding: "10px 12px", color: "#6b7280" }}>{a.old_value}</td>
                  <td style={{ padding: "10px 12px", color: "#34d399" }}>{a.new_value}</td>
                  <td style={{ padding: "10px 12px", color: "#3a5a3a", fontSize: 11 }}>{new Date(a.created_at).toLocaleString("es-UY")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      )}

      {subTab === "usuarios" && <UsersSection canModify={canModify} canDeleteUser={canDeleteUser} />}

      <nav className="subtab-bar-mobile">
        {SUPPORT_SUB_TABS.map(t => {
          const Icon = t.icon;
          const active = subTab === t.key;
          return (
            <button key={t.key} onClick={() => setSubTab(t.key)}
              style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "9px 4px 8px", background: "transparent", border: "none", color: active ? "#34d399" : "#4a6a4a", cursor: "pointer" }}>
              <Icon size={19} />
              <span style={{ fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase" }}>{t.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}


const WAITLIST_PAGE_SIZE = 50;

function WaitlistNotifyModal({ preview, error, sending, onConfirm, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div style={{ background: "#0d120d", border: "1px solid #34d399", borderRadius: 2, padding: "32px 40px", width: 480, maxWidth: "90vw" }}>
        <div style={{ fontSize: 10, color: "#34d399", letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>Confirmar envío masivo</div>
        {error ? (
          <div style={{ fontSize: 12, color: "#f87171", marginBottom: 20 }}>{error}</div>
        ) : !preview ? (
          <div style={{ fontSize: 12, color: "#3a5a3a", marginBottom: 20 }}>Cargando preview...</div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: "#c8e6c8", marginBottom: 12 }}>
              Se va a enviar un WhatsApp a <strong>{preview.pending}</strong> {preview.pending === 1 ? "usuario" : "usuarios"} de la waitlist todavía sin notificar.
            </div>
            <div style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 2, padding: "12px 16px", fontSize: 12, color: "#c8e6c8", whiteSpace: "pre-wrap", marginBottom: 12 }}>
              {preview.message}
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 20, lineHeight: 1.6 }}>
              {preview.template_name ? (
                <>Texto libre arriba para quien ya le escribió al bot en las últimas 24h; el resto recibe el template aprobado por Meta <strong style={{ color: "#8ab88a" }}>{preview.template_name}</strong> en su idioma, cuyo contenido exacto lo define Meta, no este mensaje.</>
              ) : (
                <>Sin template configurado todavía — solo llega a quien le escribió al bot en las últimas 24h, el resto no recibe nada.</>
              )}
            </div>
          </>
        )}
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={onCancel} disabled={sending} style={{ flex: 1, padding: "10px", background: "transparent", border: "1px solid #2a3a2a", borderRadius: 2, color: "#6b7280", fontSize: 12, cursor: sending ? "default" : "pointer" }}>Cancelar</button>
          <button onClick={onConfirm} disabled={sending || !preview || preview.pending === 0}
            style={{ flex: 1, padding: "10px", background: "#1a3a1a", border: "1px solid #34d399", borderRadius: 2, color: "#34d399", fontSize: 12, cursor: (sending || !preview || preview.pending === 0) ? "default" : "pointer", opacity: (sending || !preview || preview.pending === 0) ? 0.6 : 1 }}>
            {sending ? "Enviando..." : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function WaitlistSection({ canModify }) {
  const [waitlist, setWaitlist] = useState([]);
  const [total, setTotal] = useState(0);
  const [pending, setPending] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const loadWaitlist = (offset = 0) => {
    setLoadingMore(true);
    return api.getWaitlist(offset, WAITLIST_PAGE_SIZE)
      .then(data => {
        setWaitlist(prev => offset === 0 ? data.items : [...prev, ...data.items]);
        setTotal(data.total);
        setPending(data.pending);
      })
      .catch(e => console.error("[WAITLIST ERROR]", e))
      .finally(() => setLoadingMore(false));
  };

  useEffect(() => {
    loadWaitlist(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UX-1: antes disparaba el envío masivo con un window.confirm que solo
  // mostraba una cantidad, sin dejar ver qué mensaje (o qué template) le iba
  // a llegar de verdad a cada usuario.
  const handleNotifyClick = async () => {
    setShowModal(true);
    setPreview(null);
    setPreviewError(null);
    try {
      const data = await api.getWaitlistNotifyPreview();
      setPreview(data);
    } catch (e) {
      setPreviewError(e.message || "No se pudo cargar el preview.");
    }
  };

  const handleConfirmNotify = async () => {
    setNotifying(true);
    try {
      const result = await api.notifyWaitlist();
      setNotifyResult(result);
      setShowModal(false);
      loadWaitlist(0);
    } catch (e) {
      setPreviewError(e.message || "Error al enviar.");
    }
    setNotifying(false);
  };

  return (
    <div>
      {showModal && (
        <WaitlistNotifyModal
          preview={preview}
          error={previewError}
          sending={notifying}
          onConfirm={handleConfirmNotify}
          onCancel={() => setShowModal(false)}
        />
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontSize: 11, color: "#4a6a4a" }}>
          {total} {total === 1 ? "usuario registrado" : "usuarios registrados"}
          {pending > 0 && ` · ${pending} ${pending === 1 ? "sin notificar" : "sin notificar"}`}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {notifyResult && (
            <div style={{ fontSize: 11, color: "#34d399" }}>
              ✅ Enviados: {notifyResult.sent} · Fallidos: {notifyResult.failed}
            </div>
          )}
          {canModify && (
            <button onClick={handleNotifyClick} disabled={notifying || pending === 0}
              style={{ padding: "6px 14px", fontSize: 10, letterSpacing: 1, background: notifying ? "transparent" : "#1a3a1a", border: "1px solid #34d399", borderRadius: 2, color: "#34d399", cursor: "pointer", textTransform: "uppercase", opacity: (notifying || pending === 0) ? 0.6 : 1 }}>
              {notifying ? "Enviando..." : `Notificar nuevos (${pending})`}
            </button>
          )}
        </div>
      </div>
      {waitlist.length === 0 ? (
        <div style={{ color: "#3a5a3a", fontSize: 12, padding: "24px 0" }}>Sin usuarios en la waitlist aún.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 400 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #1a2a1a" }}>
                {["Teléfono", "Fecha de registro", "Notificado"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, letterSpacing: 2, color: "#3a5a3a", textTransform: "uppercase", fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {waitlist.map((w, i) => (
                <tr key={w.phone} style={{ borderBottom: "1px solid #111811", background: i % 2 === 0 ? "transparent" : "#0a0f0a" }}>
                  <td style={{ padding: "10px 12px", color: "#8ab88a", fontFamily: "monospace" }}>{w.phone}</td>
                  <td style={{ padding: "10px 12px", color: "#3a5a3a", fontSize: 11 }}>{new Date(w.created_at).toLocaleString("es-UY")}</td>
                  <td style={{ padding: "10px 12px", color: w.notified_at ? "#34d399" : "#3a5a3a", fontSize: 11 }}>
                    {w.notified_at ? new Date(w.notified_at).toLocaleString("es-UY") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {waitlist.length < total && (
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button onClick={() => loadWaitlist(waitlist.length)} disabled={loadingMore}
                style={{ padding: "6px 14px", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", background: "transparent", border: "1px solid #1a2a1a", borderRadius: 2, color: "#4a6a4a", cursor: loadingMore ? "default" : "pointer" }}>
                {loadingMore ? "Cargando..." : `Cargar más (${total - waitlist.length})`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProviderToggleModal({ provider, busy, onConfirm, onCancel }) {
  const deactivating = provider.status === "active";
  const color = deactivating ? "#f87171" : "#34d399";
  const uncovered = provider.uncovered_currencies_if_disabled || [];
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div style={{ background: "#0d120d", border: `1px solid ${color}`, borderRadius: 2, padding: "32px 40px", width: 460 }}>
        <div style={{ fontSize: 10, color, letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>
          {deactivating ? "Confirmar desactivación" : "Confirmar activación"}
        </div>
        <div style={{ fontSize: 13, color: "#c8e6c8", marginBottom: 12, lineHeight: 1.6 }}>
          {deactivating
            ? `Desactivar ${provider.display_name}. Las operaciones nuevas y las pendientes de confirmar van a fallar. Los usuarios siguen viendo su saldo e historial. Monedas que quedan sin proveedor activo: ${uncovered.length ? uncovered.join(", ") : "ninguna"}.`
            : `Activar ${provider.display_name}. Las cuentas de los usuarios se crean cuando cada uno vuelva a escribir.`}
        </div>
        {provider.error && (
          <div style={{ fontSize: 11, color: "#f87171", marginBottom: 16 }}>{provider.error}</div>
        )}
        <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
          <button onClick={onCancel} disabled={busy} style={{ flex: 1, padding: "10px", background: "transparent", border: "1px solid #2a3a2a", borderRadius: 2, color: "#6b7280", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
          <button onClick={onConfirm} disabled={busy} style={{ flex: 1, padding: "10px", background: deactivating ? "#3a1a1a" : "#0d2b1a", border: `1px solid ${color}`, borderRadius: 2, color, fontSize: 12, cursor: "pointer" }}>
            {busy ? "..." : deactivating ? "Desactivar" : "Activar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetConfirmModal({ phone, onConfirm, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div style={{ background: "#0d120d", border: "1px solid #f87171", borderRadius: 2, padding: "32px 40px", width: 440 }}>
        <div style={{ fontSize: 10, color: "#f87171", letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>Confirmar reset</div>
        <div style={{ fontSize: 13, color: "#c8e6c8", marginBottom: 12 }}>Se va a borrar por completo la cuenta de:</div>
        <div style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 2, padding: "8px 20px", fontSize: 18, color: "#f87171", fontFamily: "monospace", marginBottom: 20, display: "inline-block" }}>
          {phone}
        </div>
        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 24, lineHeight: 1.6 }}>
          Esto elimina KYC, transacciones, cuentas de payout y el usuario en sí, en producción. No se puede deshacer.
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "10px", background: "transparent", border: "1px solid #2a3a2a", borderRadius: 2, color: "#6b7280", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: "10px", background: "#3a1a1a", border: "1px solid #f87171", borderRadius: 2, color: "#f87171", fontSize: 12, cursor: "pointer" }}>Resetear</button>
        </div>
      </div>
    </div>
  );
}

function UsersSection({ canModify, canDeleteUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const loadUsers = (q) => {
    setLoading(true);
    api.getUsers(q)
      .then(data => { setUsers(data); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers(search.trim() || undefined);
  };

  const handleToggleActive = async (u) => {
    setBusyId(u.id);
    try {
      await api.toggleUserActive(u.id, !u.is_active);
      setUsers(list => list.map(x => x.id === u.id ? { ...x, is_active: !u.is_active } : x));
    } catch (e) {
      setError(e.message);
    }
    setBusyId(null);
  };

  const confirmReset = async () => {
    const u = resetTarget;
    setResetTarget(null);
    setBusyId(u.id);
    try {
      await api.resetUser(u.phone);
      setUsers(list => list.filter(x => x.id !== u.id));
    } catch (e) {
      setError(e.message);
    }
    setBusyId(null);
  };

  return (
    <div>
      {resetTarget && (
        <ResetConfirmModal phone={resetTarget.phone} onConfirm={confirmReset} onCancel={() => setResetTarget(null)} />
      )}

      <form onSubmit={handleSearch} style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por teléfono, alias o nombre"
          style={{ flex: 1, minWidth: 220, maxWidth: 320, background: "#0d120d", border: "1px solid #1a2a1a", borderRadius: 2, padding: "7px 10px", color: "#c8e6c8", fontSize: 12, outline: "none" }} />
        <button type="submit" style={{ padding: "6px 14px", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", background: "transparent", border: "1px solid #1a2a1a", borderRadius: 2, color: "#4a6a4a", cursor: "pointer" }}>Buscar</button>
        {search && (
          <button type="button" onClick={() => { setSearch(""); loadUsers(); }}
            style={{ padding: "6px 14px", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", background: "transparent", border: "1px solid #1a2a1a", borderRadius: 2, color: "#4a6a4a", cursor: "pointer" }}>Limpiar</button>
        )}
      </form>

      {error && <div style={{ color: "#f87171", fontSize: 11, marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div style={{ color: "#3a5a3a", fontSize: 12, padding: "24px 0" }}>Cargando...</div>
      ) : users.length === 0 ? (
        <div style={{ color: "#3a5a3a", fontSize: 12, padding: "24px 0" }}>Sin usuarios encontrados.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 860 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #1a2a1a" }}>
                {["Teléfono", "Nombre", "Alias", "País", "KYC", "Modo", "Estado", "Creado", "Acciones"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, letterSpacing: 2, color: "#3a5a3a", textTransform: "uppercase", fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const kyc = KYC_LABELS[u.kyc_status] || { label: u.kyc_status, color: "#6b7280" };
                const busy = busyId === u.id;
                return (
                  <tr key={u.id} style={{ borderBottom: "1px solid #111811", background: i % 2 === 0 ? "transparent" : "#0a0f0a" }}>
                    <td style={{ padding: "10px 12px", color: "#8ab88a", fontFamily: "monospace" }}>{u.phone}</td>
                    <td style={{ padding: "10px 12px", color: "#e8f5e8" }}>{u.full_name || "—"}</td>
                    <td style={{ padding: "10px 12px", color: "#6b9a6b" }}>{u.alias ? `@${u.alias}` : "—"}</td>
                    <td style={{ padding: "10px 12px", color: "#6b9a6b" }}>{u.country_code}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ color: kyc.color, fontSize: 11 }}>{kyc.label}</span>
                    </td>
                    <td style={{ padding: "10px 12px", color: "#4a6a4a", fontSize: 11 }}>{u.interaction_mode}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ color: u.is_active ? "#34d399" : "#6b7280", fontSize: 11 }}>{u.is_active ? "Activo" : "Inactivo"}</span>
                    </td>
                    <td style={{ padding: "10px 12px", color: "#3a5a3a", fontSize: 11 }}>{new Date(u.created_at).toLocaleString("es-UY")}</td>
                    <td style={{ padding: "10px 12px" }}>
                      {canModify || canDeleteUser ? (
                        <div style={{ display: "flex", gap: 6 }}>
                          {canModify && (
                            <button onClick={() => handleToggleActive(u)} disabled={busy}
                              style={{ padding: "4px 10px", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", background: "transparent", border: "1px solid #2a3a2a", borderRadius: 2, color: "#8ab88a", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                              {u.is_active ? "Desactivar" : "Activar"}
                            </button>
                          )}
                          {canDeleteUser && (
                            <button onClick={() => setResetTarget(u)} disabled={busy}
                              style={{ padding: "4px 10px", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", background: "transparent", border: "1px solid #4a1a1a", borderRadius: 2, color: "#f87171", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
                              Reset
                            </button>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "#2a3a2a", fontSize: 10 }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BusinessView({ user }) {
  const [period, setPeriod] = useState("7d");
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [fees, setFees] = useState(null);
  const [feeStatus, setFeeStatus] = useState("pending");
  const [supportFees, setSupportFees] = useState(null);
  const canModify = (ROLE_PERMISSIONS[user.role] || {}).modify_params;

  useEffect(() => {
    let controller = new AbortController();
    const load = () => {
      controller.abort();
      controller = new AbortController();
      api.getSupportFees(feeStatus, controller.signal).then(setSupportFees).catch(e => !isAbortError(e) && console.error(e));
    };
    load();
    const interval = setInterval(load, DASHBOARD_REFRESH_SECONDS * 1000);
    return () => { clearInterval(interval); controller.abort(); };
  }, [feeStatus]);

  useEffect(() => {
    api.getStats(period).then(setStats).catch(console.error);
  }, [period]);

  useEffect(() => {
    let controller = new AbortController();
    const load = () => {
      controller.abort();
      controller = new AbortController();
      api.getFees(period, controller.signal).then(setFees).catch(e => !isAbortError(e) && console.error(e));
    };
    load();
    const interval = setInterval(load, DASHBOARD_REFRESH_SECONDS * 1000);
    return () => { clearInterval(interval); controller.abort(); };
  }, [period]);

  useEffect(() => {
    let controller = new AbortController();
    const load = () => {
      controller.abort();
      controller = new AbortController();
      api.getMonthly(controller.signal).then(setMonthly).catch(e => !isAbortError(e) && console.error(e));
    };
    load();
    const interval = setInterval(load, DASHBOARD_REFRESH_SECONDS * 1000);
    return () => { clearInterval(interval); controller.abort(); };
  }, []);

  const PERIODS = [
    { key: "3d", label: "3 días" },
    { key: "7d", label: "7 días" },
    { key: "30d", label: "30 días" },
    { key: "90d", label: "3 meses" },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: "#0d120d", border: "1px solid #1a2a1a", borderRadius: 2, padding: "10px 14px", fontSize: 11 }}>
        <div style={{ color: "#6b9a6b", marginBottom: 6 }}>{label}</div>
        {payload.map(p => (
          <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
            {p.name}: {p.name === "Transacciones" ? p.value : fmt(p.value)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {PERIODS.map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)}
            style={{ padding: "6px 16px", fontSize: 11, letterSpacing: 1, background: period === p.key ? "#1a3a1a" : "transparent", border: `1px solid ${period === p.key ? "#34d399" : "#1a2a1a"}`, borderRadius: 2, color: period === p.key ? "#34d399" : "#4a6a4a", cursor: "pointer" }}>
            {p.label}
          </button>
        ))}
      </div>

      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
          <StatCard label="Transacciones" value={stats.tx_count} />
          <StatCard label="Volumen" value={fmt(stats.volume)} accent="#a3e635" />
          <StatCard label="Ingresos" value={fmt(stats.revenue)} accent="#34d399" />
          <StatCard label="Nuevos usuarios" value={stats.new_users} />
          <StatCard label="Monto promedio" value={fmt(stats.avg_amount)} />
          <StatCard label="Tasa de éxito" value={stats.success_rate == null ? "Sin datos" : `${stats.success_rate}%`} accent="#34d399" />
        </div>
      )}
      {stats && (
        <div style={{ fontSize: 10, color: "#4a6a4a", marginTop: -12, marginBottom: 24 }}>
          Ingresos incluye el fee del proveedor; el fee de Zer0 está en la sección siguiente.
        </div>
      )}

      <Section title="Flujo en vivo" subtitle="Transferencias entre paises" collapsible defaultCollapsed>
        <LiveTransactionMap />
      </Section>

      <Section title="Fee de Zer0: cobrado vs pendiente" collapsible>
        {!fees || fees.by_provider.length === 0 ? (
          <div style={{ color: "#3a5a3a", fontSize: 12, padding: "24px 0" }}>Sin datos aún.</div>
        ) : (
          <div>
            {[{ unit: "USD", data: fees.usd }, { unit: "ZUS", data: fees.zus }]
              .filter(u => u.unit === "USD" || u.data.billed > 0)
              .map(({ unit, data }) => {
                const show = v => (unit === "USD" ? fmt(v) : `${fmtNum(v)} ZUS`);
                return (
                  <div key={unit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 16 }}>
                    <StatCard label={`Facturado (${unit})`} value={show(data.billed)} />
                    <StatCard label={`Cobrado (${unit})`} value={show(data.collected)} accent="#34d399" />
                    <StatCard label={`Pendiente (${unit})`} value={show(data.pending)} accent="#a3e635" />
                    <StatCard label={`Pasado a pérdida (${unit})`} value={show(data.written_off)} accent="#f87171" />
                  </div>
                );
              })}
            {fees.unpriced_count > 0 && (
              <div style={{ fontSize: 11, color: "#a3e635", marginBottom: 12 }}>
                {fees.unpriced_count} fees sin equivalente en USD (moneda sin corredor): no están en los totales.
              </div>
            )}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #1a2a1a" }}>
                    {["Proveedor", "Unidad", "Cobrado", "Pendiente", "Pasado a pérdida", "Operaciones"].map(h => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, letterSpacing: 2, color: "#3a5a3a", textTransform: "uppercase", fontWeight: 400 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fees.by_provider.map((p, i) => {
                    const show = v => (p.unit === "USD" ? fmt(v) : `${fmtNum(v)} ZUS`);
                    return (
                      <tr key={`${p.provider}-${p.unit}`} style={{ borderBottom: "1px solid #111811", background: i % 2 === 0 ? "transparent" : "#0a0f0a" }}>
                        <td style={{ padding: "10px 12px", color: "#8ab88a" }}>{p.provider}</td>
                        <td style={{ padding: "10px 12px", color: "#e8f5e8" }}>{p.unit}</td>
                        <td style={{ padding: "10px 12px", color: "#34d399" }}>{show(p.collected)}</td>
                        <td style={{ padding: "10px 12px", color: "#a3e635" }}>{show(p.pending)}</td>
                        <td style={{ padding: "10px 12px", color: "#f87171" }}>{show(p.written_off)}</td>
                        <td style={{ padding: "10px 12px", color: "#e8f5e8" }}>{p.count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      <Section title="Fees pendientes y pasados a perdida" collapsible>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[{ key: "pending", label: "Pendientes" }, { key: "written_off", label: "Pasados a pérdida" }].map(o => (
            <button key={o.key} onClick={() => { setSupportFees(null); setFeeStatus(o.key); }}
              style={{ padding: "6px 16px", fontSize: 11, letterSpacing: 1, background: feeStatus === o.key ? "#1a3a1a" : "transparent", border: `1px solid ${feeStatus === o.key ? "#34d399" : "#1a2a1a"}`, borderRadius: 2, color: feeStatus === o.key ? "#34d399" : "#4a6a4a", cursor: "pointer" }}>
              {o.label}
            </button>
          ))}
        </div>
        {!supportFees || supportFees.items.length === 0 ? (
          <div style={{ color: "#3a5a3a", fontSize: 12, padding: "24px 0" }}>Sin datos aún.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ fontSize: 10, color: "#4a6a4a", marginBottom: 8 }}>
              Mostrando {supportFees.items.length} de {supportFees.total}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #1a2a1a" }}>
                  {["Transacción", "Proveedor", "Monto", "Equivalente (USD / ZUS)", "Intentos", "Próximo intento", "Último error"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, letterSpacing: 2, color: "#3a5a3a", textTransform: "uppercase", fontWeight: 400 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {supportFees.items.map((f, i) => (
                  <tr key={f.transaction_id} style={{ borderBottom: "1px solid #111811", background: i % 2 === 0 ? "transparent" : "#0a0f0a" }}>
                    <td style={{ padding: "10px 12px", color: "#8ab88a" }}>{String(f.transaction_id).slice(0, 8)}</td>
                    <td style={{ padding: "10px 12px", color: "#e8f5e8" }}>{f.provider}</td>
                    <td style={{ padding: "10px 12px", color: "#e8f5e8" }}>{fmtNum(f.amount)} {f.currency}</td>
                    <td style={{ padding: "10px 12px", color: "#34d399" }}>{f.amount_usd == null ? "Sin equivalente" : f.is_points ? `${fmtNum(f.amount_usd)} ZUS` : fmt(f.amount_usd)}</td>
                    <td style={{ padding: "10px 12px", color: "#e8f5e8" }}>{f.attempts}</td>
                    <td style={{ padding: "10px 12px", color: "#6b9a6b" }}>{f.next_attempt_at ? new Date(f.next_attempt_at).toLocaleString("es-UY") : "-"}</td>
                    <td style={{ padding: "10px 12px", color: "#f87171" }}>{f.last_error || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Facturación y margen por mes" collapsible>
        {monthly.length === 0 ? (
          <div style={{ color: "#3a5a3a", fontSize: 12, padding: "24px 0" }}>Sin datos aún.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={monthly} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34d399" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="margin" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a3e635" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#a3e635" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#111811" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "#3a5a3a", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#3a5a3a", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="revenue" name="Ingresos" stroke="#34d399" strokeWidth={1.5} fill="url(#revenue)" />
              <Area type="monotone" dataKey="margin" name="Margen" stroke="#a3e635" strokeWidth={1.5} fill="url(#margin)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Section>

      <Section title="Volumen de transacciones por mes" collapsible>
        {monthly.length === 0 ? (
          <div style={{ color: "#3a5a3a", fontSize: 12, padding: "24px 0" }}>Sin datos aún.</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthly} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#111811" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "#3a5a3a", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#3a5a3a", fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="tx_count" name="Transacciones" fill="#1a3a1a" stroke="#34d399" strokeWidth={1} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Section>

      <Section title="Desglose mensual" collapsible>
        {monthly.length === 0 ? (
          <div style={{ color: "#3a5a3a", fontSize: 12, padding: "24px 0" }}>Sin datos aún.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #1a2a1a" }}>
                {["Mes", "Transacciones", "Ingresos", "Margen"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, letterSpacing: 2, color: "#3a5a3a", textTransform: "uppercase", fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...monthly].reverse().map((row, i) => (
                <tr key={row.month} style={{ borderBottom: "1px solid #111811", background: i % 2 === 0 ? "transparent" : "#0a0f0a" }}>
                  <td style={{ padding: "10px 12px", color: "#8ab88a" }}>{row.month}</td>
                  <td style={{ padding: "10px 12px", color: "#e8f5e8" }}>{row.tx_count}</td>
                  <td style={{ padding: "10px 12px", color: "#34d399" }}>{fmt(row.revenue)}</td>
                  <td style={{ padding: "10px 12px", color: "#a3e635" }}>{fmt(row.margin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Waitlist" collapsible>
        <WaitlistSection canModify={canModify} />
      </Section>
    </div>
  );
}

// ── Protected Dashboard ────────────────────────────────────

function ProtectedDashboard() {
  const [user, setUser] = useState(() => api.loadStoredUser());
  const [view, setView] = useState("support");

  const perms = user ? (ROLE_PERMISSIONS[user.role] || {}) : {};

  useEffect(() => {
    if (user) {
      if (perms.view_support) setView("support");
      else if (perms.view_business) setView("business");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return <Login onLogin={setUser} />;

  return (
    <div style={{ minHeight: "100vh", background: "#080c08", fontFamily: "'DM Mono', monospace", color: "#c8e6c8" }}>
      <style>{`
        @import url('/fonts/fonts.css');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; height: 4px; background: #0d120d; }
        ::-webkit-scrollbar-thumb { background: #1a2a1a; border-radius: 2px; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        button:hover { opacity: 0.85; }
		@media (min-width: 640px) { .user-info { display: block !important; } }
		@media (max-width: 640px) { .hide-mobile { display: none; } }
		.subtab-bar-mobile { display: none; }
		@media (max-width: 640px) {
		  .subtab-bar-desktop { display: none !important; }
		  .subtab-bar-mobile {
		    display: flex;
		    position: fixed;
		    bottom: 0; left: 0; right: 0;
		    background: #0a0f0a;
		    border-top: 1px solid #1a2a1a;
		    z-index: 50;
		    padding-bottom: env(safe-area-inset-bottom);
		  }
		  .support-view-root { padding-bottom: 68px; }
		}
      `}</style>

      <div style={{ borderBottom: "1px solid #1a2a1a", padding: "0 16px", display: "flex", alignItems: "center", height: 52, gap: 4, flexWrap: "nowrap", overflow: "hidden" }}>
        <div style={{ fontSize: 10, letterSpacing: 4, color: "#34d399", textTransform: "uppercase", marginRight: 32 }}>ZER0</div>
        <div style={{ display: "flex", gap: 2 }}>
          {perms.view_support && (
            <button onClick={() => setView("support")}
              style={{ padding: "6px 16px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", background: view === "support" ? "#1a2a1a" : "transparent", border: "none", borderBottom: view === "support" ? "1px solid #34d399" : "1px solid transparent", color: view === "support" ? "#34d399" : "#4a6a4a", cursor: "pointer" }}>
              Soporte
            </button>
          )}
          {perms.view_business && (
            <button onClick={() => setView("business")}
              style={{ padding: "6px 16px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", background: view === "business" ? "#1a2a1a" : "transparent", border: "none", borderBottom: view === "business" ? "1px solid #34d399" : "1px solid transparent", color: view === "business" ? "#34d399" : "#4a6a4a", cursor: "pointer" }}>
              Negocio
            </button>
          )}
          <button onClick={() => window.open("/simulator", "_blank")}
            style={{ padding: "6px 10px", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", background: "transparent", border: "none", borderBottom: "1px solid transparent", color: "#4a6a4a", cursor: "pointer" }}>
            <span className="hide-mobile">Simulador </span>↗
          </button>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, 		flexShrink: 0 }}>
		  <div style={{ fontSize: 10, color: "#4a6a4a", display: "none" }} className="user-info">
			{user.full_name} <span style={{ color: "#2a4a2a" }}>· {user.role}</span>
		  </div>
		  <button onClick={() => { api.logout(); setUser(null); }}
			style={{ padding: "4px 10px", fontSize: 9, letterSpacing: 1, background: "transparent", border: "1px solid #1a2a1a", borderRadius: 2, color: "#4a6a4a", cursor: "pointer", textTransform: "uppercase", flexShrink: 0 }}>
			Salir
		  </button>
		</div>
      </div>

      <div style={{ padding: "28px 16px", maxWidth: 1400, margin: "0 auto" }}>
        {view === "support" && <SupportView user={user} />}
        {view === "business" && <BusinessView user={user} />}
      </div>
    </div>
  );
}

// ── Error boundary ─────────────────────────────────────────
// BUG-11: un rol no reconocido (o cualquier otro error de render inesperado)
// tiraba el dashboard entero a pantalla en blanco, sin ningún feedback.

class DashboardErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[DashboardErrorBoundary]", error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", background: "#080c08", color: "#c8e6c8", fontFamily: "'DM Mono', monospace", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ maxWidth: 420, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "#f87171", marginBottom: 12 }}>Ocurrió un error inesperado en el dashboard.</div>
            <div style={{ fontSize: 11, color: "#4a6a4a", marginBottom: 20 }}>{this.state.error.message}</div>
            <button onClick={() => window.location.reload()}
              style={{ padding: "8px 16px", fontSize: 11, background: "transparent", border: "1px solid #34d399", borderRadius: 2, color: "#34d399", cursor: "pointer" }}>
              Recargar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── App ────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardErrorBoundary><ProtectedDashboard /></DashboardErrorBoundary>} />
        <Route path="/simulator" element={<Simulator />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}